/** @jsx jsx */
import React from 'react'
import styled from '@emotion/styled'
import { jsx } from '@emotion/react'

import FormErrorCtx from './FormErrorCtx'
import IconCheckmark from '../../svg/IconCheckmark'
import InvisibleInput from '../framework/components/InvisibleInput'
import useInputCheckbox from '../framework/hooks/useInputCheckbox'
import { InputCheckboxProps, InputWithVariant } from '../framework/interfaces'

const SIZE = '20px'
const SIZE_WIZARD = '25px'
const SIZE_LARGE = '29px'

interface CheckboxProps extends InputWithVariant {
  isChecked?: boolean
  isDisabled?: boolean
}

interface CheckboxLabelProps {
  isDisabled?: boolean
  isClickable?: boolean
}

const CheckboxLabel = styled.label<CheckboxLabelProps>(
  ({ theme }) => theme.fontSmooth,
  ({ theme }) => ({
    ...theme.fonts.inputText2,
    color: theme.colors.text,
    fontFamily: theme.fonts.families.body,
    lineHeight: '1.2em',
    textAlign: 'left',
  }),
  ({ isDisabled, theme }) =>
    !!isDisabled && {
      color: theme.colors.iconGray,
    },
  ({ isClickable }) =>
    !!isClickable && {
      cursor: 'pointer',
    }
)

export const Checkbox = styled.div<CheckboxProps>(
  (props) => ({
    alignItems: 'center',
    backgroundColor: 'white',
    border: `1px solid ${props.theme.colors.iconGray}`,
    borderRadius: '5px',
    display: 'flex',
    flex: '0 0 auto',
    height: SIZE,
    justifyContent: 'center',
    marginRight: '12px',
    marginTop: '0.1em',
    position: 'relative',
    transition: 'background 0.25s linear',
    width: SIZE,
    ':focus-within': props.theme.borders.focus,
  }),
  (props) =>
    props.variant === 'wizard' && {
      height: SIZE_WIZARD,
      width: SIZE_WIZARD,
    },
  (props) =>
    props.variant === 'large' && {
      height: SIZE_LARGE,
      width: SIZE_LARGE,
    },
  (props) =>
    !!props.isChecked &&
    props.variant !== 'outline' && {
      backgroundColor: props.theme.colors.main.primary,
    },
  (props) =>
    !!props.isDisabled &&
    !!props.isChecked && {
      backgroundColor: props.theme.colors.grays.grayLight,
    },
  (props) =>
    props.variant === 'outline' && {
      height: SIZE_LARGE,
      width: SIZE_LARGE,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: props.theme.colors.iconGray,
    },
  (props) =>
    !!props.isDisabled && {
      borderColor: props.theme.colors.grays.grayLight,
    }
)

interface Props extends InputCheckboxProps, InputWithVariant {
  Icon?: React.FC<any>
}

const InputCheckbox: React.FC<Props> = (props) => {
  const {
    className,
    errors,
    label,
    LabelComponent,
    name,
    onChange,
    style,
    value,
    variant,
    Icon = IconCheckmark,
  } = props
  const {
    ctx,
    elemId,
    isDisabled,
    isChecked,
    canAutoSubmit,
    onSubmit,
  } = useInputCheckbox(props)
  const { formState } = ctx

  const outline = variant === 'outline'
  return (
    <div css={{ display: 'inline-block' }} className={className}>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          marginBottom: '1em',
          ...(!!style && style),
        }}
      >
        <Checkbox
          isChecked={isChecked}
          isDisabled={isDisabled}
          variant={variant}
        >
          {!!isChecked && (
            <Icon variant={outline ? 'primary' : 'white'} width={'14px'} />
          )}
          <InvisibleInput
            disabled={isDisabled}
            id={elemId}
            name={name}
            type="checkbox"
            value={value}
            ref={ctx.register}
            onChange={(e) => {
              if (!!canAutoSubmit) {
                ctx.setValue('auto_submit', name)
                onSubmit(ctx.getValues())
              }
              if (!!onChange) {
                onChange(e)
              }
              return value
            }}
          />
        </Checkbox>
        {!!label && (
          <CheckboxLabel
            css={{
              display: 'inline-block',
              marginBottom: '0px',
            }}
            isDisabled={isDisabled}
            htmlFor={elemId}
            isClickable={true}
          >
            {label}
          </CheckboxLabel>
        )}
        {!!LabelComponent && LabelComponent}
      </div>
      <FormErrorCtx name={name} disabled={isDisabled} errors={errors} />
    </div>
  )
}

export default InputCheckbox
